// @givmo/assistant-panel — the conversation.
//
// ══ WHO SAID WHAT IS CARRIED BY LAYOUT, NOT BY LABELS (D-EP-37) ════════════════════════════════
//
// A person's own messages are right-aligned tinted bubbles. A reply is plain left-aligned body text:
// no bubble, no avatar, no name, no "AI" mark, no "generated with…" footer — nothing on a reply says
// who or what wrote it. The alignment alone is unambiguous, and it is the whole attribution system on
// this surface. The mechanical guard is `assistant-panel.test.tsx` → "speaker attribution is LAYOUT,
// never a label"; adding one here is a ruling violation, not a design change.
//
// ══ WHAT A REPLY MAY OFFER (D-EP-38/D8) ════════════════════════════════════════════════════════
//
// Copy, always. Insert-into-the-page, when the page behind the panel is offering a field. That is the
// entire action vocabulary — nothing here approves, publishes, sends, saves or moves anything, and
// there is no seam through which it could.
//
// ⚠ The two actions render at DIFFERENT WEIGHTS, and only the NEWEST reply gets the prominent pair.
// Inserting is an action on what you just asked for; a reply five turns up is history, and giving it
// a primary button too puts several competing calls-to-action in one scrolling column and dresses a
// plain factual answer as a draft. So: every reply carries the quiet `Copy`, and the last one
// additionally carries the prominent `Insert` + `Copy` when the page behind the panel is offering a
// field. The switch is the PAGE plus position — both facts this component actually has — never a
// guess at whether a reply "is a draft", which nothing on the wire says.
//
// ⚠ …and, for a reply the panel itself asked for, the page it was ASKED ON (`canInsertReply`). The
// server judges a reply by the context its send stamped, so a reply asked for on a page held to
// looser rules must not be handed to a field on a page held to stricter ones.

import { useCallback, useMemo } from 'react';
import {
  AssistantMarkdownText,
  BlockListRenderer,
  decodeUiBlocks,
  type BlockRenderContext,
} from '@givmo/ai';
import { Button, Icon, testIdProp } from '@givmo/design-system';
import { Copy } from 'lucide-react';
import type { ThreadMessage, ThreadStatus } from './use-assistant-thread';
import type { AssistantInsertionTarget } from './assistant-insertion-target';
import type { AssistantVocab } from './assistant-panel-config';

export interface AssistantThreadViewProps {
  messages: readonly ThreadMessage[];
  status: ThreadStatus;
  streamingText: string;
  onRetry: () => void;
  /** The page's field, when one is on screen. `null` ⇒ Copy alone. */
  insertionTarget: AssistantInsertionTarget | null;
  /** Whether the newest reply may take that field — false for a reply asked for under a different
   *  page context than the one on screen. Absent ⇒ always (the page and position decide alone). */
  canInsertReply?: (message: ThreadMessage) => boolean;
  vocab: AssistantVocab;
  blockContext: BlockRenderContext;
  testId: string;
}

export function AssistantThreadView({
  messages,
  status,
  streamingText,
  onRetry,
  insertionTarget,
  canInsertReply,
  vocab,
  blockContext,
  testId,
}: AssistantThreadViewProps) {
  // The newest reply, and `-1` when a turn is in flight — a partial answer is not something to
  // insert, so during a stream no reply carries the prominent pair.
  const lastReplyIndex = useMemo(() => {
    if (status !== 'idle') return -1;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === 'assistant') return i;
    }
    return -1;
  }, [messages, status]);

  return (
    <div className="flex flex-col gap-20" {...testIdProp(testId)}>
      {messages.map((message, index) =>
        message.role === 'user' ? (
          <UserMessage key={message.key} text={message.text} testId={`${testId}-user`} />
        ) : (
          <AssistantReply
            key={message.key}
            message={message}
            // Only the newest reply may offer the insert, and only one asked for under this page's
            // context; see the notes at the top of this file.
            insertionTarget={
              index === lastReplyIndex && (canInsertReply?.(message) ?? true)
                ? insertionTarget
                : null
            }
            vocab={vocab}
            blockContext={blockContext}
            testId={testId}
          />
        ),
      )}

      {/* The reply as it is being written. It carries no actions — there is nothing final to copy or
          insert yet, and offering them mid-sentence would hand over half a draft. */}
      {status === 'streaming' && (
        <div
          className={`${REPLY_PROSE} font-sans text-s14 leading-h15 tracking-givmo text-text`}
          aria-live="polite"
          {...testIdProp(`${testId}-streaming`)}
        >
          <AssistantMarkdownText text={streamingText} />
          <StreamingCaret />
        </div>
      )}

      {/* One quiet line where the answer would have been — not a red block. The conversation did not
          work; the page did not break. The question stays above it, so asking again is one tap and
          the person never retypes. */}
      {status === 'failed' && (
        <p
          className="font-sans text-s13 leading-h15 tracking-givmo text-text-secondary"
          role="status"
          {...testIdProp(`${testId}-failed`)}
        >
          {vocab.failedLine}{' '}
          <button
            type="button"
            onClick={onRetry}
            className="font-w600 text-brand-600 outline-none hover:underline focus-visible:underline"
            {...testIdProp(`${testId}-retry`)}
          >
            {vocab.failedRetry}
          </button>
        </p>
      )}
    </div>
  );
}

/**
 * Paragraph rhythm inside a reply.
 *
 * ⚠ The design system's reset zeroes every `<p>` margin, and a reply is the one place in this app
 * where markdown emits several of them — a two-paragraph answer (a lead-in plus the draft it hands
 * over) otherwise renders as one wall with a line break where a paragraph break belongs. The rule
 * targets adjacent siblings so a single-paragraph reply gains no leading space.
 */
const REPLY_PROSE = '[&_p+p]:mt-10';

function UserMessage({ text, testId }: { text: string; testId: string }) {
  return (
    <p
      className="max-w-[86%] self-end rounded-all10 bg-neutral-100 px-12 py-8 font-sans text-s14 leading-h15 tracking-givmo text-text"
      {...testIdProp(testId)}
    >
      {text}
    </p>
  );
}

function AssistantReply({
  message,
  insertionTarget,
  vocab,
  blockContext,
  testId,
}: {
  message: ThreadMessage;
  insertionTarget: AssistantInsertionTarget | null;
  vocab: AssistantVocab;
  blockContext: BlockRenderContext;
  testId: string;
}) {
  // A finalized reply's blocks are the richer rendering when it has them; the flat `text` projection
  // is the fallback, and the only thing a history read from before ui_blocks shipped can offer.
  const blocks = useMemo(() => decodeUiBlocks(message.uiBlocks), [message.uiBlocks]);
  return (
    <div className="flex flex-col" {...testIdProp(`${testId}-reply`)}>
      <div className={`${REPLY_PROSE} font-sans text-s14 leading-h15 tracking-givmo text-text`}>
        {blocks.length > 0 ? (
          <BlockListRenderer blocks={blocks} ctx={blockContext} />
        ) : (
          <AssistantMarkdownText text={message.text} />
        )}
      </div>
      <ReplyActions
        text={message.text}
        insertionTarget={insertionTarget}
        vocab={vocab}
        testId={testId}
      />
    </div>
  );
}

function ReplyActions({
  text,
  insertionTarget,
  vocab,
  testId,
}: {
  text: string;
  insertionTarget: AssistantInsertionTarget | null;
  vocab: AssistantVocab;
  testId: string;
}) {
  const copy = useCopyToClipboard(text);

  if (insertionTarget === null) {
    return (
      <div className="mt-10 flex gap-4">
        <button
          type="button"
          onClick={copy}
          className="inline-flex h-24 items-center gap-5 rounded-all6 px-6 font-sans text-s12 font-w500 tracking-givmo text-text-tertiary outline-none transition-colors hover:bg-surface-hover hover:text-text-secondary focus-visible:shadow-ring"
          {...testIdProp(`${testId}-copy`)}
        >
          <Icon icon={Copy} size={13} className="text-icon-muted" aria-hidden />
          {vocab.copyAction}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-12 flex flex-wrap gap-8">
      <Button
        appearance="saas"
        tone="primary"
        size="sm"
        onClick={() => insertionTarget.insert(text)}
        testId={`${testId}-insert`}
      >
        {insertionTarget.actionLabel.trim().length > 0
          ? insertionTarget.actionLabel
          : vocab.insertAction}
      </Button>
      <Button
        appearance="saas"
        tone="secondary"
        size="sm"
        onClick={copy}
        leadingIcon={<Icon icon={Copy} size={14} aria-hidden />}
        testId={`${testId}-copy`}
      >
        {vocab.copyAction}
      </Button>
    </div>
  );
}

/**
 * Copy a reply to the clipboard.
 *
 * Silent on failure by design: the clipboard is unavailable in an insecure context and can be denied
 * outright, and a person who wanted a copy is better served by trying again than by an error banner
 * over a conversation. The text is still on screen and still selectable either way.
 */
function useCopyToClipboard(text: string): () => void {
  return useCallback(() => {
    void globalThis.navigator?.clipboard?.writeText(text).catch(() => undefined);
  }, [text]);
}

/**
 * The blinking cursor at the end of a partial reply.
 *
 * `motion-reduce:animate-none` leaves a static mark for a reader who asked for less motion — the
 * caret still shows where the text ends, it simply stops blinking.
 */
function StreamingCaret() {
  return (
    <span
      aria-hidden
      className="ml-2 inline-block h-[15px] w-[2px] animate-pulse bg-text-strong align-[-2px] motion-reduce:animate-none"
    />
  );
}
